import { api } from "./client";

export interface AnnotationOut {
  id: number;
  image_id: number;
  class_id: number | null;
  shape_type: string;
  coordinates: unknown;
  source: string;
  confidence: number;
  reviewed: boolean;
}

export interface AnnotationWrite { coordinates?: unknown; class_id?: number | null; reviewed?: boolean }

export const listAnnotations = (imageId: number) => api.get<AnnotationOut[]>(`/images/${imageId}/annotations`);
export const createAnnotation = (imageId: number, a: { class_id: number | null; shape_type: string; coordinates: unknown; source: string; confidence?: number }) =>
  api.post<AnnotationOut>(`/images/${imageId}/annotations`, { json: { confidence: 1.0, ...a } });
export const updateAnnotation = (id: number, patch: AnnotationWrite) => api.put<AnnotationOut>(`/annotations/${id}`, { json: patch });
export const deleteAnnotation = (id: number) => api.del<void>(`/annotations/${id}`);
/** POST /projects/{id}/annotations/mark-reviewed — a person's chosen images; `dry_run` counts without changing anything. */
export interface BulkReviewResult {
  dry_run: boolean; images_requested: number; images_not_found: number; images_affected: number;
  annotations_marked: number; annotations_skipped_unlabelled: number;
}
export const markImagesReviewed = (pid: number, body: { image_ids: number[]; class_ids?: number[]; dry_run?: boolean }) =>
  api.post<BulkReviewResult>(`/projects/${pid}/annotations/mark-reviewed`, { json: body });
/** POST /projects/{id}/images/mark-empty — the chosen images hold none of the project's objects (or, with `empty: false`, take that back). */
export interface MarkEmptyResult {
  dry_run: boolean; empty: boolean; images_requested: number; images_not_found: number; images_marked: number;
  annotations_deleted: number; images_skipped_reviewed: number;
}
export const markImagesEmpty = (pid: number, body: { image_ids: number[]; empty?: boolean; dry_run?: boolean }) =>
  api.post<MarkEmptyResult>(`/projects/${pid}/images/mark-empty`, { json: body });
export const imageBlob = (imageId: number, signal?: AbortSignal) => api.get<Blob>(`/images/${imageId}/data`, { as: "blob", signal });

/** POST /annotations/{id}/measure — a four-corner polygon in millimetres, by scale or by depth + FOV. */
export interface MeasureOut {
  annotation_id: number; length_mm: number; breadth_mm: number; squareness: number; edges: number[];
  model: string; scale_mm_per_px: number; depth: number | null; fov_degrees: number | null; focal_length_px: number | null;
}
export const measureAnnotation = (id: number, body: { scale_mm_per_px?: number; depth?: number; fov_degrees?: number }) =>
  api.post<MeasureOut>(`/annotations/${id}/measure`, { json: body });

/** POST /annotations/{id}/measure-cloud — length, breadth and height from the points under the outline. */
export interface CloudMeasureOut {
  annotation_id: number; length_mm: number; breadth_mm: number; height_mm: number; coverage: number; points_used: number;
  flatness: number; clipped: boolean; enclosing_length_mm?: number; enclosing_breadth_mm?: number; bias_applied?: number; footprint_deg?: number;
}
export const measureCloud = (id: number, bias = 0) => api.post<CloudMeasureOut>(`/annotations/${id}/measure-cloud?bias=${bias}`);
